import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { STRIPE_TEST_KEY, FLAVOR } from "../../helpers/constants";
import { useIsMobile } from "../../helpers/customHooks";
import { useContextData } from "../../components/ContextData";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

/* Providers */
import { MapTogglesProvider } from "../Map/ToggleContext";
import { SidebarProvider } from "../Sidebar/SiderbarContext";
import { GeofenceProvider } from "../Map/GeofenceMap/GeofenceContext";

/* Layout */
import SidebarNew from "../Sidebar/SidebarNew";
import SidebarMobileNew from "../Sidebar/SidebarMobileNew";
import MapComponent from "../Map/Map";

/* Pages */
import Login from "../../pages/Login";
import SignupNew from "../../pages/SignupNew";
import SignupInviteNew from "../../pages/SignupInviteNew";
import ForgotPassword from "../../pages/ForgotPassword";
import ResetPassword from "../../pages/ResetPassword";

import DevicesNewMobile from "../../pages/DevicesNewMobile";
import EventsNewMobile from "../../pages/EventsNewMobile";
import GeofenceNew from "../../pages/GeofenceNew";
import SettingsNew from "../../pages/SettingsNew";
import Messages from "../../pages/Messages";
import MobileMap from "../../pages/MobileMap";
import SubscriptionsNew from "../../pages/SubscriptionsNew";
import StolenMode from "../../pages/StolenMode";
import SubLanding from "../../pages/SubLanding";

const stripePromise = loadStripe(STRIPE_TEST_KEY);

/* ---------------- ROUTE GROUPS ---------------- */

const TrackingRoutes = ({ MOBILE }) => (
  <Routes>
    {MOBILE && <Route path="/map" element={<MobileMap />} />}
    <Route path="/devices" element={<DevicesNewMobile />} />
    <Route path="/events" element={<EventsNewMobile />} />
    <Route path="/geofence" element={<GeofenceNew />} />
    <Route
      path="/subscriptions"
      element={
        <Elements stripe={stripePromise}>
          <SubscriptionsNew />
        </Elements>
      }
    />
    <Route path="/messages" element={<Messages />} />
    <Route path="/settings" element={<SettingsNew />} />
    <Route path="/reset/:token" element={<ResetPassword />} />
    <Route path="*" element={<Navigate to="/devices" replace />} />
  </Routes>
);

const BikeRoutes = TrackingRoutes;

const AlcoRoutes = ({ MOBILE }) => (
  <Routes>
    {MOBILE && <Route path="/map" element={<MobileMap />} />}
    <Route path="/" element={<Navigate to="/devices" replace />} />
    <Route path="/devices" element={<DevicesNewMobile />} />
    <Route path="/events" element={<EventsNewMobile />} />
    <Route path="/settings" element={<SettingsNew />} />
    <Route path="*" element={<Navigate to="/devices" replace />} />
  </Routes>
);

const LoggedOutRoutes = () => (
  <Routes>
    <Route path="/" element={<Navigate to="/login" replace />} />
    <Route path="/login" element={<Login />} />
    <Route path="/signup" element={<SignupNew />} />
    <Route path="/signup/:token" element={<SignupInviteNew />} />
    <Route path="/forgot" element={<ForgotPassword />} />
    <Route path="/reset/:token" element={<ResetPassword />} />
    <Route path="*" element={<Navigate to="/login" replace />} />
  </Routes>
);


const StolenModeRoutes = () => (
  <Routes>
    <Route path="/stolenmode/:token" element={<StolenMode />} />
  </Routes>
);

const DataplanLanding = ({ MOBILE }) => (
  <Routes>
    <Route
      path="/activate/:token"
      element={
		<SidebarProvider>
			{MOBILE ? <SidebarMobileNew /> : <SidebarNew />}

			<div className="d-flex flex-row main-wrapper">
				<Elements stripe={stripePromise}>
				<SubLanding />
				</Elements>
			</div>
		</SidebarProvider>
      }
    />
  </Routes>
);

const ResetPassRoutes = () => (
  <Routes>
    <Route path="/reset/:token" element={<ResetPassword />} />
  </Routes>
);

/* ---------------- MAIN ---------------- */

const Routing = () => {
  const MOBILE = useIsMobile();
  const location = useLocation();
  const { accessToken } = useContextData();

  const isStolenMode = location.pathname.includes("stolenmode");
  const isDataplanLanding = location.pathname.includes("/activate");
  const isReset = location.pathname.includes("/reset");

  const isAuthed = Boolean(accessToken);

  const hideMapRoutes = new Set([
    "/settings",
    "/messages",
    "/subscriptions",
    "/statistics",
    "/stolenmode"
  ]);

  const shouldShowMap =
    !MOBILE && !hideMapRoutes.has(location.pathname);

  /* --------- EARLY RETURNS --------- */

	if (isDataplanLanding) return <DataplanLanding MOBILE={MOBILE} />;
  if (isStolenMode) return <StolenModeRoutes />;
  if (!isAuthed) return <LoggedOutRoutes />;
  if(isReset) return <ResetPassRoutes />;
  const flavorRoutes = {
    tracking: <TrackingRoutes MOBILE={MOBILE} />,
    bike: <BikeRoutes MOBILE={MOBILE} />,
    alco: <AlcoRoutes MOBILE={MOBILE} />,
  };

  return (
    <MapTogglesProvider>
      <SidebarProvider>
        {MOBILE ? <SidebarMobileNew /> : <SidebarNew />}

        <div className="d-flex flex-row main-wrapper">
          <GeofenceProvider>
            {flavorRoutes[FLAVOR] ?? null}
            {shouldShowMap && <MapComponent />}
          </GeofenceProvider>
        </div>
      </SidebarProvider>
    </MapTogglesProvider>
  );
};

export default Routing;
